import React, { MouseEventHandler, ReactElement, useCallback } from "react";
import { Trans } from "@lingui/macro";
import {
  BaseCard,
  BaseCardTitle,
  BaseCardBody,
  BaseCardFooter,
  Button,
  ButtonAsButtonProps,
  CardImageAspectRatio,
  BaseCardSanityImage,
  BaseCardSanityImageProps
} from "@silversea-ssc/ui-components-react";

import { ComponentBackgroundColor } from "types/componentBackground";
import { isPresent } from "src/utils/checks";

interface BaseProps {
  title: string | ReactElement | undefined;
  description: string | ReactElement | undefined;
  imageAltText?: string;
  image?: BaseCardSanityImageProps["image"];
  imageRatio?: CardImageAspectRatio;
  backgroundColor?: ComponentBackgroundColor;
  trackingName?: string;
}

export interface LinkCardProps extends BaseProps {
  url?: string;
  onClick?: never;
  button?: never;
}

export interface ButtonCardProps extends BaseProps {
  onClick?: ButtonAsButtonProps["onClick"];
  url?: never;
  button?: never;
}

export interface CustomButtonCardProps extends BaseProps {
  onClick?: never;
  url?: never;
  button: ReactElement;
}

const WrapWithLink: React.FC<{
  href?: string;
  onClick?: ButtonAsButtonProps["onClick"];
  rel?: string;
  "data-content-piece": string;
}> = ({ href, onClick, children, ...otherProps }) => {
  const onKeyDown = useCallback(
    (e) => {
      if (onClick && (e.code == "Space" || e.code == "Enter")) onClick(e);
    },
    [onClick]
  );

  if (isPresent(href))
    return (
      <a href={href} {...otherProps}>
        {children}
      </a>
    );
  else if (isPresent(onClick))
    return (
      <div
        onClick={(onClick as unknown) as MouseEventHandler<HTMLDivElement>}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="button"
        aria-pressed="false"
        style={{ cursor: "pointer" }}
      >
        {children}
      </div>
    );
  else return <React.Fragment>{children}</React.Fragment>;
};

const LinkCard: React.FC<
  LinkCardProps | ButtonCardProps | CustomButtonCardProps
> = ({
  title,
  description,
  image,
  imageAltText,
  url,
  onClick,
  button,
  backgroundColor = "white",
  trackingName,
  imageRatio = "card"
}) => {
  const altText =
    imageAltText || (typeof title == "string" ? title : undefined);

  return (
    <BaseCard background={backgroundColor} trackingName={trackingName}>
      <WrapWithLink
        href={url}
        onClick={onClick}
        rel="nofollow"
        data-content-piece="more_image"
      >
        <BaseCardSanityImage image={image} ratio={imageRatio} alt={altText} />
      </WrapWithLink>
      {title && (
        <BaseCardTitle>
          <WrapWithLink
            href={url}
            onClick={onClick}
            data-content-piece="more_title"
          >
            {title}
          </WrapWithLink>
        </BaseCardTitle>
      )}
      <BaseCardBody>{description}</BaseCardBody>
      {url && (
        <BaseCardFooter>
          <Button
            data-content-piece="more_button"
            variant="link"
            href={url}
            iconName="arrow-right"
            rel="nofollow"
          >
            <Trans id="cta-discover-more">Discover more</Trans>
          </Button>
        </BaseCardFooter>
      )}
      {onClick && (
        <BaseCardFooter>
          <Button
            renderAs="button"
            data-content-piece="more_button"
            variant="link"
            iconName="arrow-right"
            onClick={onClick}
          >
            <Trans id="cta-discover-more">Discover more</Trans>
          </Button>
        </BaseCardFooter>
      )}
      {button && <BaseCardFooter>{button}</BaseCardFooter>}
    </BaseCard>
  );
};

export default React.memo(LinkCard);
